import { gql } from "@apollo/client"
import { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { useQuery } from "@apollo/client"
import TaskList from "./TaskList"
import "./MyTasksGroupedList.css"

export const AllTasksQuery = gql`query AllTasks($orderBy:[String], $first: Int, $after: String, $assignedToIds: [ID], $statuses: [String], $boardIds: [ID], $searchTerm: String, $isArchived: Boolean, $incidentId: ID, $dueDateGte: String, $dueDateLte: String, $dueDateIsNull: Boolean, $filters: [FilterInput]){
    allTasks(orderBy: $orderBy, first: $first, after: $after, assignedToIds: $assignedToIds, statuses: $statuses, boardIds: $boardIds, searchTerm: $searchTerm, isArchived: $isArchived, incidentId: $incidentId, dueDateGte: $dueDateGte, dueDateLte: $dueDateLte, dueDateIsNull: $dueDateIsNull, filters: $filters){
        edges{
            node{
                id
            }
        }
        pageInfo{
            hasNextPage
            endCursor
            length
        }
    }
    
}`
const MY_TASK_GROUPS = [
    { key: "withoutDate", title: "Without Date", colorClass: "task-group-withoutDate" },
    { key: "overdue", title: "Overdue", colorClass: "task-group-overdue" },
    { key: "today", title: "Today", colorClass: "task-group-today" },
    { key: "thisWeek", title: "This Week", colorClass: "task-group-thisWeek" },
    { key: "nextWeek", title: "Next Week", colorClass: "task-group-nextWeek" },
    { key: "later", title: "Later", colorClass: "task-group-later" },
]

function buildGroupDateFilters(groupKey) {
    const today = moment().startOf("day")
    const tomorrow = today.clone().add(1, "day")
    const endOfWeek = today.clone().endOf("isoWeek")
    const startOfNextWeek = today.clone().add(1, "week").startOf("isoWeek")
    const endOfNextWeek = today.clone().add(1, "week").endOf("isoWeek")
    const afterNextWeek = endOfNextWeek.clone().add(1, "day")

    if (groupKey === "withoutDate") {
        return { dueDateIsNull: true }
    }
    if (groupKey === "overdue") {
        return { dueDateIsNull: false, dueDateLte: today.clone().subtract(1, "day").format("YYYY-MM-DD") }
    }
    if (groupKey === "today") {
        const day = today.format("YYYY-MM-DD")
        return { dueDateIsNull: false, dueDateGte: day, dueDateLte: day }
    }
    if (groupKey === "thisWeek") {
        return { dueDateIsNull: false, dueDateGte: tomorrow.format("YYYY-MM-DD"), dueDateLte: endOfWeek.format("YYYY-MM-DD") }
    }
    if (groupKey === "nextWeek") {
        return { dueDateIsNull: false, dueDateGte: startOfNextWeek.format("YYYY-MM-DD"), dueDateLte: endOfNextWeek.format("YYYY-MM-DD") }
    }
    return { dueDateIsNull: false, dueDateGte: afterNextWeek.format("YYYY-MM-DD") }
}

function MyTasksGroupSection({ group, groupIndex, collapsed, onToggle, currentUserId, filterValues, orderBy, updateOrderBy, columnConfig, onCountReady, filters }) {
    const { data: groupTasksData, loading: groupTasksLoading } = useQuery(AllTasksQuery, {
        variables: {
            assignedToIds: [currentUserId],
            ...filterValues,
            dueDateGte: group.filters.dueDateGte || null,
            dueDateLte: group.filters.dueDateLte || null,
            dueDateIsNull: group.filters.dueDateIsNull,
            orderBy,
            first: 1,
            filters,
        },
        fetchPolicy: "cache-first",
    })

    const groupCount = groupTasksData?.allTasks?.pageInfo?.length
    const resolvedGroupCount = typeof groupCount === "number" ? groupCount : 0
    const summaryText = resolvedGroupCount === 0 ? "No tasks" : `${resolvedGroupCount} task${resolvedGroupCount === 1 ? "" : "s"}`

    useEffect(() => {
        if (groupTasksLoading || typeof groupCount !== "number") {
            return
        }
        onCountReady(group.key, groupCount)
    }, [group.key, groupCount, groupTasksLoading, onCountReady])

    return (
        <div key={group.key} className={`${group.colorClass} task-group-section ${groupIndex === 0 ? "task-group-first" : ""} ${collapsed ? "task-group-section--collapsed" : "task-group-section--expanded"}`}>
            <button
                type="button"
                className="task-group-toggle"
                onClick={() => onToggle(group.key)}
                aria-expanded={!collapsed}
            >
                <span className="task-group-toggle__left">
                    <span className="task-group-toggle__icon" aria-hidden="true">
                        <i className={`fa ${collapsed ? "fa-chevron-right" : "fa-chevron-down"}`} />
                    </span>
                    <span className="task-group-toggle__content">
                        <span className="task-group-title-row">
                            <span className="task-group-title">{group.title}</span>
                            <span className="task-group-count">{resolvedGroupCount}</span>
                        </span>
                        <span className="task-group-summary">{summaryText}</span>
                    </span>
                </span>
                <span className="task-group-toggle__state">{collapsed ? "Show" : "Hide"}</span>
            </button>
            {!collapsed && (
                <div className="task-group-body">
                    <TaskList
                        assignedToIds={[currentUserId]}
                        {...filterValues}
                        dueDateGte={group.filters.dueDateGte || null}
                        dueDateLte={group.filters.dueDateLte || null}
                        dueDateIsNull={group.filters.dueDateIsNull}
                        orderBy={orderBy}
                        updateOrderBy={updateOrderBy}
                        columnConfig={columnConfig}
                        filters={filters}
                    />
                </div>
            )}
        </div>
    )
}

function MyTasksGroupedList({ currentUserId, filterValues, orderBy, updateOrderBy, columnConfig, filters }) {
    const [collapsedGroups, setCollapsedGroups] = useState({})
    const [groupCounts, setGroupCounts] = useState({})
    const groups = useMemo(() => MY_TASK_GROUPS.map(group => ({ ...group, filters: buildGroupDateFilters(group.key) })), [])

    const toggleGroup = (groupKey) => {
        setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
    }

    const handleCountReady = (groupKey, count) => {
        setGroupCounts((prev) => {
            if (prev[groupKey] === count) return prev
            return { ...prev, [groupKey]: count }
        })
        setCollapsedGroups((prev) => {
            if (Object.prototype.hasOwnProperty.call(prev, groupKey)) {
                return prev
            }
            return { ...prev, [groupKey]: count === 0 }
        })
    }

    return (
        <div className="TaskList my-tasks-grouped-list">
            {groups.map((group, groupIndex) => (
                <MyTasksGroupSection
                    key={group.key}
                    group={group}
                    groupIndex={groupIndex}
                    collapsed={Object.prototype.hasOwnProperty.call(collapsedGroups, group.key) ? !!collapsedGroups[group.key] : ((groupCounts[group.key] ?? 0) === 0)}
                    onToggle={toggleGroup}
                    onCountReady={handleCountReady}
                    currentUserId={currentUserId}
                    filterValues={filterValues}
                    orderBy={orderBy}
                    updateOrderBy={updateOrderBy}
                    columnConfig={columnConfig}
                    filters={filters}
                />
            ))}
        </div>
    )
}

export default MyTasksGroupedList
